import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';

interface ProjectExchangeRateData {
  project_rate: number;
  project_rate_updated: string | null;
  service_rate: number;
  service_rate_source: string;
}

/**
 * Hook to fetch project's exchange rate
 */
export function useProjectExchangeRate(projectId?: string) {
  return useQuery<ProjectExchangeRateData>({
    queryKey: ['projectExchangeRate', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const response = await apiClient.get(`/projects/${projectId}/exchange-rate`);
      return response.data.data;
    },
    enabled: !!projectId,
    staleTime: 0, // Always consider data stale to ensure updates are reflected
    cacheTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Always refetch when component mounts
  });
}

/**
 * Hook to update project's exchange rate
 */
export function useUpdateProjectExchangeRate(projectId?: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (rate: number) => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const response = await apiClient.post(`/projects/${projectId}/exchange-rate`, {
        exchange_rate: rate
      });
      return response.data;
    },
    onSuccess: async (data, variables) => {
      // Immediately invalidate and refetch to get the latest data from server
      // This ensures all components using this data get the update
      await queryClient.invalidateQueries({ 
        queryKey: ['projectExchangeRate', projectId],
        refetchType: 'all' // Force refetch even if data is considered fresh
      });
    }
  });
}